// messagegroup.js

import React, { useMemo, useState } from "react";
import Message, { formatMessageDetails, HighlightedText } from "./Message.js";
import '../css/MessageGroup.css';
import { normalizeEpochMillis, parseTransactTimeToEpochMillis } from "../utils/time.js";

const MESSAGE_TYPE_PRIORITY = {
  quoteRequest: 1,
  tradeQuoteRequest: 1,
  quote: 2,
  tradeQuote: 2,
  dealRequest: 3,
  tradeDealRequest: 3,
  executionReport: 4,
  tradeExecutionReport: 4,
  error: 5,
  tradeError: 5,
  quoteCancel: 6,
  tradeQuoteCancel: 6,
};

const toSortableTimestamp = (message) => {
  const rawValue = message?.messageTime ?? message?.timestamp;
  const parsedValue = normalizeEpochMillis(rawValue);
  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return parseTransactTimeToEpochMillis(message?.transactTime);
};

const getMessagePriority = (message) => (
  MESSAGE_TYPE_PRIORITY[message?.type] ?? 0
);

const isPreferredMessage = (candidate, current) => {
  if (!current) {
    return true;
  }

  const candidateTimestamp = toSortableTimestamp(candidate);
  const currentTimestamp = toSortableTimestamp(current);

  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp;
  }

  const candidatePriority = getMessagePriority(candidate);
  const currentPriority = getMessagePriority(current);

  if (candidatePriority !== currentPriority) {
    return candidatePriority > currentPriority;
  }

  return true;
};

const MessageGroup = ({ quoteRequestID, messages = [], searchTerm = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const latestMessage = useMemo(() => {
    if (messages.length === 0) {
      return null;
    }

    return messages.reduce((latest, message) => (
      isPreferredMessage(message, latest) ? message : latest
    ), null);
  }, [messages]);

  const latestMessageContent = latestMessage ? formatMessageDetails(latestMessage) : "";
  const latestMessageType = latestMessage?.type ?? "";

  return (
    <div>
      <div
        className="message-group-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{isOpen ? '-' : '+'}</span>
        {`${quoteRequestID}: `}
        <span className={`message ${latestMessageType}`}>
          <HighlightedText text={latestMessageContent} searchTerm={searchTerm} />
        </span>
      </div>
      {isOpen && (
        <div>
          {messages.map((message, index) => (
            <Message key={index} message={message} searchTerm={searchTerm} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageGroup;
